import { type ChangeEvent, useState } from 'react'

import {
  FvButton,
  Icon,
  type IconProps,
  ValidatedForm,
} from '@fv/client-components'
import {
  addressFuncs,
  validateEmailInput,
  validateEmailListInput,
} from '@fv/client-core'
import {
  type ClientAddressLookupResult,
  type ShipType,
  shipTypeOptions,
} from '@fv/client-types'
import {
  type AddressLookupContext,
  type FlatbedHandlingType,
  flatbedHandlingTypeLabels,
  flatbedHandlingTypes,
  type LocationAccessorialType,
  type Workflow,
} from '@fv/models'

import { DividedContent } from '@/components/DividedContent'
import { AddressInput } from '@/components/inputs/AddressInput'
import CheckboxField from '@/components/inputs/CheckboxField'
import { ContactPhoneInput } from '@/components/inputs/ContactPhoneInput'
import { InputGroup, InputGroupWrapper } from '@/components/inputs/InputGroup'
import { Pinline } from '@/components/Pinline'
import { TabLink } from '@/components/TabLink'
import { hasInsideOption, hasLiftgateOption } from '@/utils/locationFuncs'

import { HasPermission } from '../auth'
import { useHasPermission } from '../auth/hooks'
import { usePreferredWorkflows } from '../profiile/hooks'
import { DockTimeInput } from '../shipment-location-form/DockTimeInput'
import { addressMapper } from './addressFuncs'
import type { FormAddress } from './types'
import { useAddressBook, useAddressBookConfig } from './useAddressBook'
import { useCacheFormattedAddress } from './useAddressSearch'
import { useCreateAddress } from './useCreateAddress'
import { useDeleteAddress } from './useDeleteAddress'
import { useUpdateAddress } from './useUpdateAddress'

const shipTypeOptionsList = [...shipTypeOptions]

const flatbedHandlingTypeOptions = [
  '', // Empty option for "not applicable"
  ...flatbedHandlingTypes,
] as const

const workflowDisplays: Record<
  Workflow,
  {
    icon: IconProps['icon']
    label: string
  }
> = {
  parcel: { icon: 'box', label: 'Parcel' },
  ltl: { icon: 'forklift', label: 'LTL' },
  truckload: { icon: 'truck-moving', label: 'Truckload' },
}

type Props = {
  initialValues?: ClientAddressLookupResult
  context?: AddressLookupContext
}

export const ShippingAddressForm = ({ initialValues, context }: Props) => {
  const workflows = usePreferredWorkflows()
  const {
    cancel,
    deleteAddress,
    getCheckboxProps,
    getInputProps,
    handleChanges,
    isDeleting,
    isEditing,
    isLoading,
    onValidSubmit,
    setWorkflow,
    values,
    workflow,
  } = useAddressForm(initialValues, workflows[0])

  const readOnly = !useHasPermission('manage-addresses')
  const canHaveInside = hasInsideOption(values.shipType)
  const canHaveLiftgate = hasLiftgateOption(values.shipType)

  const workflowEnabled = (w: Workflow) =>
    w === 'truckload' || values.country !== 'mx'

  return (
    <ValidatedForm
      className="flex h-full flex-col justify-between"
      onValidSubmit={onValidSubmit}
    >
      <div className="-mx-6 overflow-auto px-6 pb-8">
        <h6 className="mb-0">
          <Icon icon="building" className="text-fv-gray-dark" />
          {isEditing ? (
            <span>Edit address</span>
          ) : (
            <span>Add a new address</span>
          )}
        </h6>
        <Pinline className="mb-6 mt-2" />
        <div className="flex flex-wrap">
          <div className="form-row">
            <InputGroup
              className="basis-full"
              inputProps={getInputProps('company', {
                placeholder: 'Company name',
                readOnly,
              })}
              inputType="text"
              label="Company"
              required
            />

            <InputGroup
              className="basis-1/3"
              inputProps={getInputProps('address', {
                placeholder: 'Street address',
                readOnly: values.isAccountLocation || readOnly,
                disabled: values.isAccountLocation,
              })}
              inputType="text"
              label="Street address"
              required
            />

            <InputGroup
              className="basis-1/3"
              inputProps={getInputProps('address2', {
                readOnly: values.isAccountLocation || readOnly,
                disabled: values.isAccountLocation,
              })}
              inputType="text"
              label="Address 2"
            />

            <InputGroupWrapper
              className="basis-1/3"
              label="City, state, zip"
              name="cityStateZip"
              required
            >
              <AddressInput
                context={context}
                formatAddress={addressFuncs.cityStateZip}
                isClearable={false}
                location={values}
                name="cityStateZip"
                onLocationChange={x => {
                  if (isLoading || isDeleting) return

                  if (x?.country === 'mx') {
                    setWorkflow('truckload')
                  }

                  handleChanges({
                    city: x?.city || '',
                    country: x?.country || 'us',
                    postalCode: x?.postalCode || '',
                    state: x?.state || '',
                    adminArea3: x?.adminArea3,
                  })
                }}
                placeholder="Zip/postal code"
                readOnly={values.isAccountLocation || readOnly}
                required
              />
            </InputGroupWrapper>

            <InputGroup
              className="basis-1/3"
              inputProps={getInputProps('contactName', {
                readOnly,
              })}
              inputType="text"
              label="Contact name"
            />

            <ContactPhoneInput
              className="basis-1/3"
              disabled={isLoading || isDeleting}
              name="contactPhone"
              readOnly={readOnly}
              onChange={handleChanges}
              values={{
                contactPhone: values.contactPhone,
                contactPhoneExt: values.contactPhoneExt,
              }}
            />

            <InputGroup
              className="basis-1/3"
              inputProps={getInputProps('contactEmail', {
                readOnly,
              })}
              inputType="text"
              label="Contact email"
            />

            <InputGroup
              className="flex-1"
              inputProps={getInputProps('shipType', {
                options: shipTypeOptionsList,
                disabled: readOnly,
              })}
              inputType="select"
              label="Location type"
              required
            />

            <DockTimeInput
              className="basis-[10rem]"
              disabled={isLoading || isDeleting || readOnly}
              label="Open time"
              value={values}
              name="opensAt"
              onChange={handleChanges}
            />

            <DockTimeInput
              className="basis-[10rem]"
              disabled={isLoading || isDeleting || readOnly}
              label="Close time"
              value={values}
              name="closesAt"
              onChange={handleChanges}
            />

            <InputGroup
              className="basis-full"
              inputProps={{
                ...getInputProps('instructions'),
                readOnly,
              }}
              inputType="text"
              label="Special instructions"
            />
            <InputGroup
              helpText='Instructions for your users pertaining to this address. i.e. - "Prefers Carrier X" - click for more info'
              helpHref="https://blog.freightview.com/utilizing-the-address-book/#special-instructions-internal-notes"
              className="basis-full"
              inputProps={{
                ...getInputProps('internalNotes'),
                maxLength: 250,
                readOnly,
              }}
              inputType="text"
              label="Internal notes"
            />
            <InputGroup
              className="basis-full"
              inputProps={{
                ...getInputProps('bolFollowers'),
                readOnly,
              }}
              inputType="text"
              label="Share shipments with"
            />

            <CheckboxField
              className="form-group"
              {...getInputProps('sendBOL', {
                label: 'Send copy of bill of lading',
                readOnly,
              })}
            />
          </div>

          <div className="bg-fv-beer-xlight basis-full border border-fv-beer-medium p-4 mt-2">
            <DividedContent theme="beer" className="basis-full mb-5">
              <span className="first:pl-0">Above address options for</span>
              {workflows
                .map(w => ({
                  key: w,
                  display: workflowDisplays[w],
                }))
                .map(({ key, display }) => (
                  <TabLink
                    key={key}
                    icon={display.icon}
                    isActive={workflow === key}
                    disabled={!workflowEnabled(key)}
                    onClick={() => workflowEnabled(key) && setWorkflow(key)}
                    tooltip={
                      workflowEnabled(key)
                        ? undefined
                        : 'MX support currently not available'
                    }
                  >
                    {display.label}
                  </TabLink>
                ))}
            </DividedContent>
            <Pinline rule className="basis-full" />

            {workflow === 'ltl' && (
              <>
                {(canHaveInside || canHaveLiftgate) && (
                  <div className="mb-4 flex space-x-8">
                    {canHaveLiftgate && (
                      <CheckboxField
                        {...getCheckboxProps('liftgate', 'Liftgate required')}
                        disabled={readOnly}
                      />
                    )}

                    {canHaveInside && (
                      <CheckboxField
                        {...getCheckboxProps(
                          'inside',
                          'Inside pickup/delivery',
                        )}
                        disabled={readOnly}
                      />
                    )}
                  </div>
                )}

                <div className="flex space-x-8">
                  <CheckboxField
                    {...getCheckboxProps(
                      'notify',
                      'Call/notify before delivery',
                    )}
                    disabled={readOnly}
                  />

                  <CheckboxField
                    {...getCheckboxProps('schedule', 'Delivery appointment')}
                    disabled={readOnly}
                  />

                  <CheckboxField
                    {...getCheckboxProps(
                      'sort-and-segregate',
                      'Sort & segregate',
                    )}
                    disabled={readOnly}
                  />
                </div>
              </>
            )}

            {workflow === 'parcel' && (
              <div className="flex space-x-8">
                <CheckboxField
                  {...getCheckboxProps('no-signature', 'No signature')}
                  disabled={readOnly}
                />

                <CheckboxField
                  {...getCheckboxProps('signature-required', 'Signature')}
                  disabled={readOnly}
                />

                <CheckboxField
                  {...getCheckboxProps(
                    'adult-signature-required',
                    'Adult signature',
                  )}
                  disabled={readOnly}
                />

                {values.shipType === 'residential' && (
                  <CheckboxField
                    {...getCheckboxProps(
                      'indirect-signature-required',
                      'Indirect signature',
                    )}
                    disabled={readOnly}
                  />
                )}
              </div>
            )}

            {workflow === 'truckload' && (
              <>
                <div className="flex space-x-8">
                  <CheckboxField
                    {...getCheckboxProps('schedule', 'Appointment required')}
                    disabled={readOnly}
                  />

                  <CheckboxField
                    {...getCheckboxProps('driver-assist', 'Driver assist')}
                    disabled={readOnly}
                  />

                  <CheckboxField
                    {...getCheckboxProps('notify', 'Arrival notification')}
                    disabled={readOnly}
                  />
                  <CheckboxField
                    {...getCheckboxProps('twic', 'TWIC required')}
                  />
                </div>
                <Pinline rule dashed />
                <InputGroup
                  inputProps={getInputProps('flatbedHandlingType', {
                    options: flatbedHandlingTypeOptions.map(opt => ({
                      value: opt,
                      text: opt
                        ? flatbedHandlingTypeLabels[opt as FlatbedHandlingType]
                        : '',
                    })),
                    disabled: readOnly,
                  })}
                  inputType="select"
                  className="mb-0"
                  label="Load / Unload type for flatbed shipments at this address"
                  helpText="Preferred loading / unloading type for flatbed shipments at this address. Leave blank if not applicable."
                />
              </>
            )}
          </div>
        </div>
      </div>

      <HasPermission name="manage-addresses">
        <div className="flyout-footer-actions flex items-center">
          <FvButton
            icon={isLoading ? 'spinner' : isEditing ? 'pen-alt' : 'plus'}
            theme="primary"
            type="submit"
          >
            {isEditing ? 'Update' : 'Add'} address and apply
          </FvButton>

          {isEditing && !initialValues?.isAccountLocation && (
            <FvButton
              icon={isDeleting ? 'spinner' : 'trash'}
              onClick={deleteAddress}
              theme="default"
            >
              Delete address
            </FvButton>
          )}

          <FvButton icon="times" onClick={cancel} theme="plain">
            Cancel
          </FvButton>
        </div>
      </HasPermission>
    </ValidatedForm>
  )
}

function useAddressForm(
  initialValues?: ClientAddressLookupResult,
  initialWorkflow?: Workflow,
) {
  const addressId = initialValues?._id
  const cacheFormattedAddress = useCacheFormattedAddress()
  const selectAddress = useAddressBook(s => s.selectAddress)
  const addressBookEdit = useAddressBook(s => s.edit)
  const config = useAddressBookConfig()
  const isEditing = !!addressId

  const createAddress = useCreateAddress(config)
  const updateAddress = useUpdateAddress(config)
  const removeAddress = useDeleteAddress(config)
  const isDeleting = removeAddress.isPending
  const isLoading = createAddress.isPending || updateAddress.isPending
  const disabled = isDeleting || isLoading

  const [workflow, setWorkflow] = useState<Workflow>(initialWorkflow ?? 'ltl')
  const [values, setValues] = useState<FormAddress>(() =>
    addressMapper.buildFormAddress(initialValues),
  )

  function handleChanges(changes: Partial<FormAddress>) {
    setValues(prev => ({ ...prev, ...changes }))
  }

  function toggleAccessorial(
    workflow: Workflow,
    name: LocationAccessorialType | 'no-signature',
  ) {
    setValues(prev => {
      let nextAccessorials = prev.mappedAccessorials[workflow].slice()
      const hasAccessorial = nextAccessorials.some(a => a.key === name)

      // Can only select one signature option at a time
      if (name === 'no-signature' || name.includes('signature')) {
        nextAccessorials = nextAccessorials.filter(
          a => !a.key.includes('signature'),
        )

        if (!hasAccessorial && name !== 'no-signature') {
          nextAccessorials.push({ key: name })
        }
      } else if (
        // Cannot select `notify` and `schedule` together for LTL
        workflow === 'ltl' &&
        (name === 'notify' || name === 'schedule')
      ) {
        nextAccessorials = nextAccessorials.filter(
          a => a.key !== 'notify' && a.key !== 'schedule',
        )

        if (!hasAccessorial) nextAccessorials.push({ key: name })
      } else {
        nextAccessorials = hasAccessorial
          ? nextAccessorials.filter(a => a.key !== name)
          : nextAccessorials.concat({ key: name })
      }

      return {
        ...prev,
        mappedAccessorials: {
          ...prev.mappedAccessorials,
          [workflow]: nextAccessorials,
        },
      }
    })
  }

  function getCheckboxProps(
    name: LocationAccessorialType | 'no-signature',
    label: string,
  ) {
    const checked =
      name === 'no-signature'
        ? values.mappedAccessorials[workflow].every(
            a => !a.key.includes('signature'),
          )
        : values.mappedAccessorials[workflow].some(a => a.key === name)

    return {
      checked,
      disabled,
      label,
      name,
      onChange: () => toggleAccessorial(workflow, name),
    }
  }

  function getInputProps(
    name: keyof Omit<FormAddress, 'accessorials' | 'charges'>,
    props?: any,
  ) {
    return {
      disabled,
      name,
      onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        if (name === 'sendBOL') {
          return handleChanges({
            sendBOL: (e.target as HTMLInputElement).checked,
          })
        }

        if (name === 'bolFollowers') {
          validateEmailListInput(e.target as HTMLInputElement)
        }

        if (name === 'contactEmail') {
          validateEmailInput(e.target as HTMLInputElement)
        }

        if (name === 'shipType') {
          const shipType = e.target.value as ShipType

          if (
            !hasInsideOption(shipType) &&
            values.mappedAccessorials.ltl.some(a => a.key === 'inside')
          ) {
            toggleAccessorial('ltl', 'inside')
          }

          if (
            !hasLiftgateOption(shipType) &&
            values.mappedAccessorials.ltl.some(a => a.key === 'liftgate')
          ) {
            toggleAccessorial('ltl', 'liftgate')
          }

          if (
            shipType !== 'residential' &&
            values.mappedAccessorials.parcel.some(
              a => a.key === 'indirect-signature-required',
            )
          ) {
            toggleAccessorial('parcel', 'indirect-signature-required')
          }
        }

        handleChanges({ [name]: e.target.value })
      },
      ...(name === 'sendBOL'
        ? {
            checked: values.sendBOL,
          }
        : {
            value: values[name],
          }),
      ...props,
    }
  }

  function deleteAddress() {
    if (isDeleting || isLoading || !addressId) return

    if (
      window.confirm(
        'Are you sure you want to delete this entry from your address book?',
      )
    ) {
      removeAddress.mutate(
        { id: addressId },
        {
          onSuccess: () => addressBookEdit(undefined),
        },
      )
    }
  }

  function onValidSubmit() {
    if (isLoading || isDeleting) return
    const opts = {
      onSuccess: (address: ClientAddressLookupResult) => {
        const formatted = addressFuncs.companyAddress(address)
        cacheFormattedAddress({ search: formatted }, address)
        selectAddress?.(address)
      },
    }

    if (isEditing) updateAddress.mutate({ ...values, id: addressId }, opts)
    else createAddress.mutate(values, opts)
  }

  return {
    cancel: () => !isDeleting && !isLoading && addressBookEdit(undefined),
    deleteAddress,
    getCheckboxProps,
    getInputProps,
    handleChanges,
    isDeleting,
    isEditing,
    isLoading,
    onValidSubmit,
    setWorkflow,
    values,
    workflow,
  }
}
